import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { watchSessionExpiry } from '@/domain/auth';
import { redirectToLogin } from '@/utils/loginRedirect';

import { useAuthStatus } from './hooks';

/**
 * Sends the user to the login page, back to this page afterwards, when an API call finds the
 * session has ended (it expired, or the user signed out in another tab). Without it the pages
 * stay up and every call fails until a reload. Renders nothing.
 */
export function SessionExpiryWatcher() {
    const { isAuthenticated } = useAuthStatus();
    const queryClient = useQueryClient();
    const signedIn = useRef(isAuthenticated);

    useEffect(() => {
        signedIn.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(
        () =>
            watchSessionExpiry({
                isSignedIn: () => signedIn.current,
                onExpired: () => {
                    // Drop the signed-in user and everything fetched for them before leaving,
                    // so the page shows nothing of theirs while the login page loads.
                    void queryClient.cancelQueries();
                    queryClient.clear();
                    redirectToLogin();
                },
            }),
        [queryClient]
    );

    return null;
}
